import {gql} from '@apollo/client';
import {Box, Button, Grid, Typography} from '@mui/material';

import React from 'react';
import {useParams} from 'react-router-dom';

import {Panel} from '../../components/Panel/Panel';

import {OneViewModelTypesEnum} from '../../schema-types/globalTypes';
import AutoField, {ActionType} from '../../components/AutoForm/AutoFieldNew';

import {TestForm} from './TestFormInputs';
import withAutoFormNew from '../../components/AutoForm/AutoFormNew';
import {useAutoField} from '../../components/AutoForm/useAutoForm';
import {OpenInCuro} from '../../utils/getCuroURL';

export const GET_NATIVE_ID_FROM_CURO = gql`
  query GetNativeIdFromCuro($modelName: OneViewModelTypesEnum!, $curoId: UUID!) {
    nativeId(modelName: $modelName, curoId: $curoId)
  }
`;

export type TestHomeFormType = {
  modelName: OneViewModelTypesEnum;
  curoId: string;
  booleanFieldStartChecked: boolean;
};

export const TestHomePage: React.FC = () => {
  const {accountCuroId} = useParams();
  return (
    <TestHomeFormWrapped
      formFields={[]}
      initialValueOverrides={{
        modelName: OneViewModelTypesEnum.ACCOUNT,
        curoId: accountCuroId,
        booleanFieldStartChecked: true,
      }}
    />
  );
};

export const TestHomePageForm: React.FC = () => {
  const [{value: accountCuroId}] = useAutoField<string>('accountCuroId');
  const [{value: reviewCuroId}] = useAutoField<string>('reviewCuroId');
  return (
    <>
      <Box mt={3} />

      <Panel title="Open account/review using curo id">
        <Box p={3}>
          <Typography variant="h5">
            To use this page, put in the curo id for what you want and then click which
            one you want
          </Typography>
          <Typography mb={2} variant="h5">
            With account curo id:
          </Typography>
          <Grid container direction="column" spacing={1}>
            <Grid item>
              <AutoField
                field={{
                  id: 'accountCuroId',
                  type: ActionType.text,
                  label: 'Enter account curo id',
                }}
              />
            </Grid>
            <Grid item>
              <Button
                disabled={!accountCuroId}
                href={`/test/test-account/${accountCuroId}`}
                variant="contained"
              >
                Go to account test page
              </Button>
            </Grid>
            <Grid item>
              <Button
                disabled={!accountCuroId}
                href={`/test/test-any-open-review/${accountCuroId}`}
                variant="contained"
              >
                Go to any open review (uses account curo Id)
              </Button>
            </Grid>
            <Grid item>
              <OpenInCuro
                variant="contained"
                curoType="account"
                curoId={accountCuroId}
                alwaysShow
                disabled={!accountCuroId}
              >
                Open account in Curo
              </OpenInCuro>
            </Grid>
          </Grid>

          <Grid mt={5} container direction="column" spacing={1}>
            <Grid item>
              <Typography mb={2} variant="h5">
                With review curo id:
              </Typography>
              <AutoField
                field={{
                  id: 'reviewCuroId',
                  type: ActionType.text,
                  label: 'Enter review curo id',
                }}
              />
            </Grid>
            <Grid item>
              <Button
                disabled={!reviewCuroId}
                href={`/test/test-review/${reviewCuroId}`}
                variant="contained"
              >
                Go to review test page
              </Button>
            </Grid>
            <Grid item>
              <OpenInCuro
                variant="contained"
                curoType="review"
                curoId={reviewCuroId}
                alwaysShow
                disabled={!reviewCuroId}
              >
                Open review in Curo
              </OpenInCuro>
            </Grid>
          </Grid>
        </Box>
      </Panel>
      <Box mt={6} />
      <TestForm />
    </>
  );
};

export const TestHomeFormWrapped = withAutoFormNew<TestHomeFormType, object>(
  TestHomePageForm
);
